import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiAddManualTransaction,
  apiDeleteManualTransaction,
  apiGetManualTransactions,
} from "@lib/api/transaction";
import AppTable from "../../../Table/AppTable";
import { FormattedMessage, useIntl } from "react-intl";
import getColumns from "./columns";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { useEffect, useState } from "react";
import { ApiParams } from "@lib/types/Api";
import { ManualTransactionModal } from "@lib/components/PageComponents/ManualTransaction";
import { toast } from "react-toastify";
import { Invoice } from "@lib/types/Billing";

type Props = {
  invoice: Invoice;
  invoiceQueryKey: string[];
};

export default function ManualTransactions({
  invoice,
  invoiceQueryKey,
}: Props) {
  const { messages } = useIntl();
  const [params, setParams] = useState<ApiParams>({
    page: 1,
    limit: 3,
    sort: "createdAt,DESC",
  });
  const query = useQuery({
    queryFn: () =>
      apiGetManualTransactions({
        filter: [`invoiceId||$eq||${invoice?.id}`],
        ...params,
      }),
    queryKey: ["invoice-manual-transactions", params],
    enabled: Boolean(invoice?.id),
    refetchOnWindowFocus: true,
  });
  const { data, isLoading, isFetching } = query;
  const apiData = data?.data?.data;

  useEffect(() => {
    if (!invoice?.id) {
      queryClient.removeQueries({
        queryKey: ["invoice-manual-transactions", params],
      });
    } else {
      query.refetch();
    }
  }, [invoice?.id]);

  const queryClient = useQueryClient();
  const addQuery = useMutation({
    mutationFn: apiAddManualTransaction,
    mutationKey: ["invoice-manual-transactions"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.createdMessage"
          values={{ name: messages["common.cashPayments"] as string }}
        />,
      );
      toggle(false);
      queryClient.invalidateQueries({
        queryKey: ["invoice-manual-transactions"],
      });
    },
  });

  const deleteQuery = useMutation({
    mutationFn: apiDeleteManualTransaction,
    mutationKey: ["invoice-manual-transactions"],
    onSuccess() {
      toast.success(
        <FormattedMessage
          id="common.deletedMessage"
          values={{ name: messages["common.cashPayments"] as string }}
        />,
      );
      toggle(false);
      queryClient.invalidateQueries({
        queryKey: ["invoice-manual-transactions"],
      });
    },
  });

  const [openModal, setModalOpen] = useState(false);
  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const columns = getColumns({
    apiData,
    messages,
    deleteQuery,
  });

  return (
    <>
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 3}
          onPageChange={(page: number, limit: number) => {
            setParams({ ...params, page, limit });
          }}
          query={query}
          plusProps={{
            onClick: () => toggle(true),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          rowKey={"id"}
          loading={isLoading || isFetching}
          dataSource={apiData}
          columns={columns}
        />
      </AppsContent>

      <ManualTransactionModal
        mutationQuery={addQuery}
        openModal={openModal}
        toggleModal={toggle}
        customer={invoice?.customer}
        invoiceId={invoice?.id}
        invoiceQueryKey={invoiceQueryKey}
      />
    </>
  );
}
